import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

// Typeahead combobox. Type directly in the box → matching options appear instantly.
// Dropdown is rendered in a portal (position: fixed) so it is never clipped by scrolling
// tables/cards. Options: { value, label, sub?, keywords?: string[] } — keywords (phone, GSTIN, HSN…) are searchable too.
export const SearchableSelect = ({ options, value, onChange, placeholder, testId, className, nextFocusId, onCreateNew }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [typing, setTyping] = useState(false);
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find(o => o.value === value);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    const digits = term.replace(/\D/g, "");
    const starts = [], contains = [];
    for (const o of options) {
      const label = (o.label || "").toLowerCase();
      const hay = [label, o.sub || "", ...(o.keywords || [])].join(" ").toLowerCase();
      if (label.startsWith(term)) starts.push(o);
      else if (hay.includes(term) || (digits.length >= 3 && hay.replace(/\D/g, "").includes(digits))) contains.push(o);
    }
    return [...starts, ...contains];
  }, [options, q]);

  const place = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight, vw = window.innerWidth;
    const width = Math.min(Math.max(r.width, 340), vw - 16);
    const left = Math.max(8, Math.min(r.left, vw - width - 8));
    const below = vh - r.bottom;
    const flip = below < 260 && r.top > below;
    const maxH = Math.max(160, Math.min(380, (flip ? r.top : below) - 16));
    setPos(flip ? { left, width, bottom: vh - r.top + 4, maxH } : { left, width, top: r.bottom + 4, maxH });
  };
  useLayoutEffect(() => { if (open) place(); /* eslint-disable-next-line */ }, [open, filtered.length]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove); };
    // eslint-disable-next-line
  }, [open]);
  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${hi}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const close = () => { setOpen(false); setTyping(false); setQ(""); };
  const focusNext = () => {
    if (!nextFocusId) return;
    setTimeout(() => document.querySelector(`[data-testid="${nextFocusId}"]`)?.focus(), 50);
  };
  const pick = (v, viaEnter = false) => {
    onChange(v);
    close();
    if (viaEnter) focusNext();
  };
  const createNew = () => { const t = q.trim(); close(); onCreateNew?.(t); };
  const onKey = (e) => {
    e.stopPropagation();
    if (e.altKey && e.key.toLowerCase() === "c" && onCreateNew) { e.preventDefault(); createNew(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) setOpen(true); else setHi(h => Math.min(h + 1, filtered.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const o = filtered[hi] || filtered[0];
      if (o) pick(o.value, true);
      else if (onCreateNew && q.trim()) createNew();
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); close(); inputRef.current?.blur(); return; }
    if (e.key === "Tab") { close(); }
  };

  const shown = typing ? q : (selected ? selected.label : "");
  const mark = (text) => {
    const term = q.trim();
    if (!term || !text) return text;
    const i = text.toLowerCase().indexOf(term.toLowerCase());
    if (i < 0) return text;
    return <>{text.slice(0, i)}<mark className="bg-[#FCEFD4] text-inherit rounded-sm px-0">{text.slice(i, i + term.length)}</mark>{text.slice(i + term.length)}</>;
  };

  return (
    <div className={`relative ${className || ""}`} ref={wrapRef}>
      <div className="relative">
        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
        <input
          ref={inputRef}
          data-testid={testId}
          role="combobox"
          aria-expanded={open}
          autoComplete="off"
          className={`field !pl-8 !pr-14 ${selected && !typing ? "font-medium" : ""}`}
          placeholder={placeholder || "Type to search…"}
          value={shown}
          onFocus={() => { setOpen(true); }}
          onClick={() => setOpen(true)}
          onChange={e => { setTyping(true); setQ(e.target.value); setOpen(true); }}
          onKeyDown={onKey}
        />
        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {selected && !typing && (
            <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => { onChange(""); setQ(""); setTyping(false); inputRef.current?.focus(); }}
              className="p-1 rounded text-ink-muted hover:text-destructive hover:bg-surface-muted" title="Clear" data-testid={`${testId}-clear`}><X size={12} /></button>
          )}
          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => { if (open) close(); else { setOpen(true); inputRef.current?.focus(); } }}
            className="p-1 rounded text-ink-muted hover:bg-surface-muted" data-testid={`${testId}-toggle`}><CaretDown size={12} /></button>
        </div>
      </div>
      {open && pos && createPortal(
        <div ref={menuRef} data-testid={`${testId}-dropdown`}
          className="fixed z-[200] bg-white border border-border rounded-md shadow-xl overflow-hidden"
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}>
          <div className="px-3 py-1.5 text-[11px] text-ink-muted border-b border-border flex justify-between bg-surface-muted/60">
            <span>{q.trim() ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}` : `${options.length} option${options.length === 1 ? "" : "s"} — type to filter`}</span>
            <span className="hidden sm:inline">↑↓ move · Enter pick · Esc close{onCreateNew ? " · Alt+C new" : ""}</span>
          </div>
          <div ref={listRef} className="overflow-y-auto py-1" style={{ maxHeight: pos.maxH }}>
            {filtered.length === 0 && <div className="px-3 py-3 text-sm text-ink-muted" data-testid={`${testId}-no-match`}>No matches for “{q}”</div>}
            {onCreateNew && q.trim() && (
              <button type="button" data-testid={`${testId}-create-new`} onMouseDown={e => e.preventDefault()} onClick={createNew}
                className="w-full text-left px-3 py-2 text-sm font-semibold text-brand hover:bg-surface-muted border-b border-border">
                + Create “{q.trim()}” <span className="text-[10px] text-ink-muted font-normal ml-1">(Alt+C)</span>
              </button>
            )}
            {filtered.slice(0, 200).map((o, idx) => (
              <button
                key={o.value}
                type="button"
                data-idx={idx}
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => setHi(idx)}
                onClick={() => pick(o.value)}
                className={`w-full text-left px-3 py-2 text-sm block hover:bg-surface-muted ${idx === hi ? "bg-surface-muted" : ""} ${o.value === value ? "font-semibold" : ""}`}
                data-testid={`${testId}-option-${o.value}`}
              >
                <div className="truncate">{mark(o.label)}</div>
                {o.sub && <div className="text-[11px] text-ink-muted truncate">{mark(o.sub)}</div>}
              </button>
            ))}
          </div>
        </div>, document.body)}
    </div>
  );
};
